import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, CreditCard, ExternalLink, CheckCircle, Shield, DollarSign, HelpCircle } from 'lucide-react';

export default function StripeHelp() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-bold text-slate-800">Setting Up Stripe Payments</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Accept Card Payments</h2>
              <p className="text-purple-200">Get paid faster with Stripe</p>
            </div>
          </div>
          <p className="text-purple-100">
            Connect your Stripe account to accept credit/debit card payments directly from your customers. 
            Funds are deposited directly to your bank account.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
              <DollarSign className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">Fast Deposits</h3>
            <p className="text-slate-600 text-sm">Funds deposited to your bank in 2 business days</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">Secure Payments</h3>
            <p className="text-slate-600 text-sm">Bank-level security & fraud protection built-in</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
              <CheckCircle className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="font-bold text-slate-800 mb-1">Easy for Customers</h3>
            <p className="text-slate-600 text-sm">One-click payments via email link</p>
          </div>
        </div>

        {/* Step by Step Guide */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-8">
          <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-purple-600" />
            Step-by-Step Setup Guide
          </h3>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-800 mb-2">Create a Stripe Account</h4>
                <p className="text-slate-600 mb-3">
                  If you don't have a Stripe account yet, create one for free. You'll need:
                </p>
                <ul className="text-slate-600 space-y-1 mb-3 text-sm">
                  <li>• Valid email address</li>
                  <li>• Business information (name, address, type)</li>
                  <li>• Bank account details for deposits</li>
                  <li>• Social Security Number (for identity verification)</li>
                </ul>
                <a 
                  href="https://dashboard.stripe.com/register" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
                >
                  Create Stripe Account <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-800 mb-2">Complete Stripe Verification</h4>
                <p className="text-slate-600 mb-3">
                  Stripe will verify your identity and business. This usually takes a few minutes. Make sure to:
                </p>
                <ul className="text-slate-600 space-y-1 text-sm">
                  <li>• Verify your email address</li>
                  <li>• Add and verify your bank account</li>
                  <li>• Complete identity verification</li>
                  <li>• Review and accept Stripe's terms of service</li>
                </ul>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-800 mb-2">Connect to MyJobTools</h4>
                <p className="text-slate-600 mb-3">
                  Once your Stripe account is ready, connect it to MyJobTools:
                </p>
                <ol className="text-slate-600 space-y-2 text-sm mb-4">
                  <li>1. Go to <strong>Profile → Payment</strong> tab</li>
                  <li>2. Select <strong>Stripe</strong> as your payment method</li>
                  <li>3. Click <strong>"Connect Stripe Account"</strong></li>
                  <li>4. You'll be redirected to Stripe to authorize the connection</li>
                  <li>5. Once complete, you'll return to MyJobTools</li>
                </ol>
                <Button 
                  onClick={() => navigate('/profile')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Go to Payment Settings
                </Button>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                <CheckCircle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <h4 className="font-bold text-slate-800 mb-2">Start Accepting Payments!</h4>
                <p className="text-slate-600">
                  Once connected, your customers can pay with card! When you click "Request Payment" on a job, 
                  customers receive a secure payment link. You'll see "Stripe Connected" in your payment settings.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Fees Info */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
          <h4 className="font-bold text-amber-800 mb-2">💰 Stripe Fees</h4>
          <p className="text-amber-700 text-sm">
            Stripe charges <strong>2.9% + 30¢</strong> per successful card transaction. 
            This is deducted automatically before funds are deposited to your bank. 
            MyJobTools does not charge any additional payment processing fees.
          </p>
        </div>

        {/* FAQ */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="text-xl font-bold text-slate-800 mb-4">Frequently Asked Questions</h3>
          
          <div className="space-y-4">
            <div>
              <h4 className="font-bold text-slate-700 mb-1">How long until I receive my money?</h4>
              <p className="text-slate-600 text-sm">
                Stripe typically deposits funds to your bank account within 2 business days after a payment is made.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-slate-700 mb-1">Is my customer's card info stored?</h4>
              <p className="text-slate-600 text-sm">
                No. MyJobTools never sees or stores card numbers. All payment data is handled securely by Stripe.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-slate-700 mb-1">Can I still use Venmo/Cash App?</h4>
              <p className="text-slate-600 text-sm">
                Yes! You can switch between payment methods anytime in your profile settings. Many contractors 
                offer customers a choice between card payments and Venmo/Cash App.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-slate-700 mb-1">What if a customer disputes a charge?</h4>
              <p className="text-slate-600 text-sm">
                Stripe handles disputes through their resolution center. You'll be notified by email and can 
                provide evidence to support your case. Having job photos and signed quotes helps!
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <Button 
            onClick={() => navigate('/profile')}
            size="lg"
            className="bg-purple-600 hover:bg-purple-700"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Set Up Stripe Now
          </Button>
          <p className="text-slate-500 text-sm mt-2">
            Available on Solo Pro and Crew plans
          </p>
        </div>
      </main>
    </div>
  );
}
